import { GAME_NAME, PROTOCOL_VERSION, SIM_TICK_RATE } from '@steelwake/shared';
import { colors } from '@steelwake/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { REVISION } from 'three';

import { api } from '../api.js';
import { browserProbeEnv, describeBackend, detectCapabilities } from '../capabilities.js';
import { useAppStore } from '../store.js';
import { Card } from '../ui.js';

export function Home() {
  const capabilities = useAppStore((s) => s.capabilities);
  const setCapabilities = useAppStore((s) => s.setCapabilities);
  const user = useAppStore((s) => s.user);

  const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);
  const [maintenance, setMaintenance] = useState('');

  useEffect(() => {
    setCapabilities(detectCapabilities(browserProbeEnv));
  }, [setCapabilities]);

  // The message of the day and the maintenance line come from the admin panel (Phase 7).
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api<{ announcement: { title: string; body: string } | null }>('/api/v1/config/announcement'),
      api<{ maintenanceMessage: string }>('/api/v1/config/flags'),
    ])
      .then(([a, f]) => {
        if (cancelled) return;
        setNotice(a.announcement);
        setMaintenance(f.maintenanceMessage);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card width={520}>
      <h1 style={{ margin: 0, letterSpacing: '0.2em', color: colors.accent }}>{GAME_NAME}</h1>
      {maintenance ? (
        <p role="status" data-testid="maintenance-message" style={{ color: colors.warning }}>
          {maintenance}
        </p>
      ) : null}
      {notice ? (
        <section
          data-testid="announcement"
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            padding: 12,
            margin: '12px 0',
          }}
        >
          <strong>{notice.title}</strong>
          <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{notice.body}</p>
        </section>
      ) : null}
      <p style={{ color: colors.textMuted }}>Practice needs no account; online play does.</p>
      <p>
        <Link to="/play" data-testid="enter-arena">
          Enter the arena
        </Link>
      </p>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px' }}>
        <dt>Renderer backend</dt>
        <dd data-testid="backend">{capabilities ? describeBackend(capabilities) : 'probing…'}</dd>
        <dt>Three.js</dt>
        <dd>r{REVISION}</dd>
        <dt>Sim tick rate</dt>
        <dd>{SIM_TICK_RATE} Hz</dd>
        <dt>Protocol</dt>
        <dd>v{PROTOCOL_VERSION}</dd>
        <dt>Pilot</dt>
        <dd data-testid="home-pilot">{user ? (user.callsign ?? user.email) : 'not signed in'}</dd>
      </dl>
      {user ? (
        <p>
          <Link to="/play/online" data-testid="find-match-link">
            Find a match online
          </Link>
        </p>
      ) : (
        <p>
          <Link to="/login">Log in</Link> or <Link to="/register">create an account</Link> to play.
        </p>
      )}
      <p style={{ color: colors.textMuted, fontSize: 13 }}>
        <Link to="/credits" data-testid="credits-link">
          Credits
        </Link>
        {' · the third-party assets the game ships and their licenses'}
      </p>
    </Card>
  );
}
